var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useMemo, useCallback, useRef, useEffect } from 'react';
import { faTrashAlt, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PolicyFormInput from '../common/PolicyFormInput';
import { formatDate } from '../../utils/common';
var Table = function (props) {
    var _a;
    var question = props.question, questionKey = props.questionKey, _b = props.value, value = _b === void 0 ? [] : _b, apiValidationErrors = props.apiValidationErrors, setFieldValue = props.setFieldValue, handleChange = props.handleChange, handleBlur = props.handleBlur, submitCount = props.submitCount, errors = props.errors;
    var columns = question.questions || [];
    var selectColumn = columns[0];
    var options = (selectColumn === null || selectColumn === void 0 ? void 0 : selectColumn.options) || [];
    var allowDuplicateOptions = (_a = question.allowDuplicateOptions) !== null && _a !== void 0 ? _a : false;
    // Ref for scrolling
    var scrollContainerRef = useRef(null);
    var gridTemplate = useMemo(function () {
        if (!columns || columns.length === 0)
            return '3fr 1fr';
        var dynamicCols = columns.map(function () { return 'minmax(200px, 1fr)'; }).join(' ');
        var lastCol = 'minmax(60px, 80px)';
        return "".concat(dynamicCols, " ").concat(lastCol);
    }, [columns]);
    useEffect(function () {
        var items = value || [];
        if (items.length === 0) {
            var emptyRow_1 = {};
            columns.forEach(function (col) {
                emptyRow_1[col.questionKey] = '';
            });
            setFieldValue(questionKey, [emptyRow_1], false);
        }
    }, []);
    var rows = useMemo(function () {
        var items = value || [];
        return items === null || items === void 0 ? void 0 : items.map(function (item, index) { return ({
            index: index,
            data: item || {}
        }); });
    }, [value]);
    var availableOptions = useMemo(function () {
        if (allowDuplicateOptions) {
            return options;
        }
        var selectedValues = rows.map(function (row) { return row.data[selectColumn.questionKey]; });
        return options.filter(function (opt) {
            return !selectedValues.includes(opt.caption) ||
                opt.caption === '' ||
                opt.caption === 'undefined';
        });
    }, [options, rows, selectColumn.questionKey, allowDuplicateOptions]);
    var getOptionsForRow = useCallback(function (rowValue) {
        if (allowDuplicateOptions) {
            return options;
        }
        var currentOption = rowValue && !availableOptions.some(function (o) { return o.caption === rowValue; })
            ? [{ value: rowValue, caption: rowValue }]
            : [];
        return __spreadArray(__spreadArray([], currentOption, true), availableOptions, true);
    }, [availableOptions, options, allowDuplicateOptions]);
    var canDeleteRow = useMemo(function () {
        return rows.length > 1;
    }, [rows.length]);
    var scrollToBottom = useCallback(function () {
        setTimeout(function () {
            var container = scrollContainerRef.current;
            if (container) {
                container.scrollTo({
                    top: container.scrollHeight,
                    behavior: 'smooth'
                });
            }
        }, 100);
    }, []);
    var handleAddRow = useCallback(function () {
        if (!allowDuplicateOptions && availableOptions.length === 0) {
            return;
        }
        var items = value || [];
        var emptyRow = {};
        // Initialize all columns with empty strings
        columns.forEach(function (col) {
            emptyRow[col.questionKey] = '';
        });
        setFieldValue(questionKey, __spreadArray(__spreadArray([], items, true), [emptyRow], false));
        // Scroll to the new row
        scrollToBottom();
    }, [
        allowDuplicateOptions,
        availableOptions,
        value,
        columns,
        questionKey,
        scrollToBottom,
    ]);
    var handleDeleteRow = useCallback(function (rowIndex) {
        if (!canDeleteRow) {
            return;
        }
        var items = value || [];
        var updatedItems = items.filter(function (_, idx) { return idx !== rowIndex; });
        setFieldValue(questionKey, updatedItems);
    }, [canDeleteRow, value, questionKey]);
    return (React.createElement("div", { className: "py-8 w-full" },
        React.createElement("div", { className: "mx-auto animate-fade-in" },
            React.createElement("div", { className: "overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0" },
                React.createElement("div", { className: "inline-block min-w-full align-middle" },
                    React.createElement("div", { className: "bg-[#F8FAFE] rounded-2xl mb-2" },
                        React.createElement("div", { className: "grid gap-4 px-6 py-4 text-left items-center", style: { gridTemplateColumns: gridTemplate } }, columns.map(function (col, index) { return (React.createElement("div", { key: "header_".concat(col.questionKey), className: "font-semibold text-slate-700 uppercase text-sm ".concat(index < columns.length - 1
                                ? 'border-r border-[#adb1b9] pr-4'
                                : '') }, col.question)); }))),
                    React.createElement("div", { ref: scrollContainerRef, className: "max-h-[400px] overflow-y-auto scroll-smooth" },
                        React.createElement("div", { className: "divide-y divide-slate-100" }, rows.map(function (row) {
                            var rowValue = row.data[selectColumn.questionKey];
                            return (React.createElement("div", { key: "row_".concat(row.index), className: "grid gap-4 px-6 py-2 items-center justify-center", style: { gridTemplateColumns: gridTemplate } },
                                columns.map(function (col, colIndex) {
                                    var _a, _b;
                                    var fieldKey = "".concat(questionKey, "_").concat(row.index, ".").concat(col.questionKey);
                                    var formikPath = "".concat(questionKey, "[").concat(row.index, "].").concat(col.questionKey);
                                    var fieldValue = row.data[col.questionKey] || '';
                                    var dataFieldPath = "".concat(questionKey, ".").concat(row.index, ".").concat(col.questionKey);
                                    return (React.createElement("div", { key: fieldKey, className: colIndex < columns.length - 1
                                            ? 'border-r border-[#F4F7FE] pr-4'
                                            : '' },
                                        React.createElement(PolicyFormInput, { questionKey: formikPath, question: col, dataField: dataFieldPath, label: "", value: fieldValue, voidValue: col.value, inputType: col.inputType, min: col.min, max: col.max, validation: col.validation, isDisabled: false, helper: col.helper, hint: col === null || col === void 0 ? void 0 : col.hint, handleChange: handleChange, handleChangeDate: function (date) {
                                                var dateObj = '';
                                                if (date) {
                                                    dateObj = formatDate(new Date(date));
                                                }
                                                setFieldValue(formikPath, dateObj);
                                            }, handleChangeAutocomplete: function (val) {
                                                setFieldValue(formikPath, val, true);
                                            }, handleBlur: handleBlur, options: col.questionKey === selectColumn.questionKey
                                                ? getOptionsForRow(rowValue)
                                                : col.options, placeholder: col.placeholder, defaultChecked: row.data[col.questionKey] || null, errors: apiValidationErrors || submitCount > 0
                                                ? (_b = (_a = errors === null || errors === void 0 ? void 0 : errors[questionKey]) === null || _a === void 0 ? void 0 : _a[row.index]) === null || _b === void 0 ? void 0 : _b[col.questionKey]
                                                : undefined, touched: apiValidationErrors || submitCount > 0, docLink: col.docLink, linkText: col.linkText, allowMultiple: col.allowMultiple, enforceNone: col.enforceNone, subHeading: col === null || col === void 0 ? void 0 : col.subHeading, variant: col === null || col === void 0 ? void 0 : col.variant })));
                                }),
                                React.createElement("div", { className: "flex justify-end items-center" },
                                    React.createElement("button", { type: "button", onClick: function () { return handleDeleteRow(row.index); }, disabled: !canDeleteRow, className: "p-2 rounded-lg transition-colors flex-shrink-0 ".concat(canDeleteRow
                                            ? 'hover:bg-red-50 cursor-pointer'
                                            : 'opacity-30 cursor-not-allowed'), "aria-label": "Delete row ".concat(row.index + 1), title: !canDeleteRow
                                            ? 'At least one row is required'
                                            : 'Delete row' },
                                        React.createElement(FontAwesomeIcon, { icon: faTrashAlt, className: "w-4 h-4 text-[#7e8084]" })))));
                        }))))),
            React.createElement("div", { className: "border-t border-slate-200 px-6 py-4 mt-4" },
                React.createElement("button", { type: "button", onClick: handleAddRow, disabled: !allowDuplicateOptions && availableOptions.length === 0, className: "flex items-center gap-2 px-4 py-2 bg-transparent text-primary rounded-lg transition-all hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed" },
                    React.createElement(FontAwesomeIcon, { icon: faPlus, className: "w-4 h-4" }),
                    React.createElement("span", { className: "text-sm font-medium" }, !allowDuplicateOptions && availableOptions.length === 0
                        ? 'No more options available'
                        : 'Add another'))))));
};
export default Table;
